import React, { useEffect, useRef, useState } from 'react';
import $ from 'jquery';
import { Icon } from '@iconify/react';
import Select from 'react-select';
import { useUserProfile } from '../masterLayout/MasterLayoutBody';
import { useValidator } from '../utils/useValidator';
import { getAll } from '../api';
import {
  createEventStatus,
  deleteEventStatus,
  getEventStatus,
  updateEventStatus,
} from '../api/event-statusAPI';

const EventStatusLayer = () => {
  const userProfile = useUserProfile();
  const [statuses, setStatuses] = useState([]);
  const [colours, setColours] = useState([]);
  const [formState, setFormState] = useState({ name: '', coloursId: '' });
  const [editingId, setEditingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);

  const tableRef = useRef(null);
  const dtRef = useRef(null);

  const validationFields = [
    { field: 'name', label: 'Name', required: true },
    { field: 'coloursId', label: 'Colour', required: true },
  ];
  const { errors, setErrors, validate } = useValidator(validationFields, formState);

  const resetDataTable = (fullyDestroy = false) => {
    if (dtRef.current) {
      dtRef.current.destroy(fullyDestroy);
      dtRef.current = null;
    }
  };

  const fetchStatuses = async () => {
    try {
      const data = await getEventStatus();
      setStatuses(data || []);
    } catch (error) {
      console.error('Error loading event statuses', error);
    }
  };

  const fetchColours = async () => {
    try {
      const data = await getAll('colours');
      setColours(data || []);
    } catch (error) {
      console.error('Error loading colours', error);
    }
  };

  useEffect(() => {
    fetchStatuses();
    fetchColours();
  }, []);

  useEffect(() => {
    resetDataTable();

    if (statuses.length) {
      dtRef.current = $(tableRef.current).DataTable({
        pageLength: 25,
        dom: 'rtp',
        responsive: false,
        language: { paginate: { first: '', last: '', next: '', previous: '' } },
      });
    }

    return () => resetDataTable(true);
  }, [statuses]);

  useEffect(() => {
    if (dtRef.current) {
      dtRef.current.search(searchTerm).draw();
    }
  }, [searchTerm]);

  const colourOptions = colours.map((colour) => ({
    value: colour.id,
    label: colour.name,
    bootstrapName: colour.bootstrapName,
  }));

  const resetForm = () => {
    setFormState({ name: '', coloursId: '' });
    setEditingId(null);
    setErrors({});
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const handleEdit = (status) => {
    setFormState({ name: status.name || '', coloursId: status.coloursId || '' });
    setEditingId(status.id);
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!validate()) return;

    const payload = {
      name: formState.name?.trim(),
      coloursId: formState.coloursId || null,
    };

    if (editingId && userProfile?.id) {
      payload.updatedBy = userProfile.id;
    } else if (userProfile?.id) {
      payload.createdBy = userProfile.id;
    }

    try {
      const savedStatus = editingId
        ? await updateEventStatus(editingId, payload)
        : await createEventStatus(payload);

      setShowModal(false);
      resetForm();
      resetDataTable();

      if (savedStatus) {
        setStatuses((prev) => {
          if (editingId) {
            return prev.map((status) => (status.id === savedStatus.id ? savedStatus : status));
          }
          return [...prev, savedStatus];
        });
      } else {
        fetchStatuses();
      }
    } catch (error) {
      console.error('Error saving event status', error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this event status?')) return;

    try {
      await deleteEventStatus(id);
      resetDataTable();
      setStatuses((prev) => prev.filter((status) => status.id !== id));
    } catch (error) {
      console.error('Error deleting event status', error);
    }
  };

  const canCreate = userProfile?.privileges?.includes('event-status:create');
  const canUpdate = userProfile?.privileges?.includes('event-status:update');
  const canDelete = userProfile?.privileges?.includes('event-status:delete');

  return (
    <div className='card h-100 p-0 rounded-0'>
      <div className='card-header d-flex justify-content-between align-items-center p-3'>
        <input
          type='text'
          className='form-control w-auto'
          placeholder='Search...'
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
        />
        {canCreate && (
          <button
            className='btn btn-primary text-sm btn-sm px-16 py-12 radius-4 d-flex align-items-center gap-2'
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
          >
            <Icon icon='ic:baseline-plus' className='icon text-xl' />
            Add Event Status
          </button>
        )}
      </div>

      <div className='card-body p-0'>
        <div className='table-responsive scroll-sm'>
          <table className='table bordered-table mb-0 w-100 rounded-0' ref={tableRef}>
            <thead>
              <tr>
                <th>Name</th>
                <th>Colour</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {statuses.length === 0 && (
                <tr>
                  <td colSpan='3' className='text-center py-4 rounded-0'>No Event Status Setup</td>
                </tr>
              )}
              {statuses.map((status) => (
                <tr key={status.id}>
                  <td><strong>{status.name}</strong></td>
                  <td>
                    {status.colours?.bootstrapName && (
                      <span
                        className={`d-inline-block rounded-1 bg-${status.colours.bootstrapName}`}
                        style={{ width: '10px', height: '10px', marginRight: '8px' }}
                      />
                    )}
                    {status.colours?.name || ''}
                  </td>
                  <td>
                    <div className='d-flex gap-2'>
                      {canUpdate && (
                        <button
                          className='w-32-px h-32-px bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center'
                          onClick={() => handleEdit(status)}
                        >
                          <Icon icon='ic:baseline-edit' className='icon text-xl line-height-1' />
                        </button>
                      )}
                      {canDelete && (
                        <button
                          className='w-32-px h-32-px bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center'
                          onClick={() => handleDelete(status.id)}
                        >
                          <Icon icon='ic:baseline-delete-outline' className='icon text-xl line-height-1' />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <div className='modal show d-block' tabIndex='-1'>
          <div className='modal-dialog'>
            <div className='modal-content'>
              <div className='modal-header'>
                <h5 className='modal-title'>{editingId ? 'Edit Event Status' : 'New Event Status'}</h5>
                <button
                  type='button'
                  className='btn-close'
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                />
              </div>
              <div className='modal-body'>
                <div className='mb-3'>
                  <label className='form-label'>Name *</label>
                  <input
                    type='text'
                    className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                    name='name'
                    value={formState.name}
                    onChange={handleInputChange}
                  />
                  {errors.name && <div className='invalid-feedback'>{errors.name}</div>}
                </div>
                <div className='mb-3'>
                  <label className='form-label'>Colour *</label>
                  <Select
                    name='coloursId'
                    classNamePrefix={`react-select ${errors.coloursId ? 'is-invalid' : ''}`}
                    value={colourOptions.find((option) => option.value === formState.coloursId) || null}
                    onChange={(option) => handleInputChange({ target: { name: 'coloursId', value: option?.value || '' } })}
                    options={colourOptions}
                    isClearable
                    placeholder='Select Colour'
                    formatOptionLabel={(option) => (
                      <div className='d-flex align-items-center gap-2'>
                        <span
                          className={`d-inline-block rounded-1 bg-${option.bootstrapName}`}
                          style={{ width: '10px', height: '10px' }}
                        />
                        {option.label}
                      </div>
                    )}
                  />
                  {errors.coloursId && <div className='text-danger mt-2 small'>{errors.coloursId}</div>}
                </div>
              </div>
              <div className='modal-footer'>
                <button
                  className='btn btn-secondary'
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                >
                  Close
                </button>
                <button className='btn btn-primary' onClick={handleSave}>Save</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventStatusLayer;
